import { useMemo, memo, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'

import { MultiLineDnd } from 'common-components'
import { TDndListItems } from 'common-components/DndList/types'

import { editorThunks } from 'store/features/editor/thunks'
import { editorSelectors } from 'store/features/editor/selectors'
import { projectDataThunks } from 'store/features/projectData/thunks'
import { projectDataSelectors } from 'store/features/projectData/selectors'

import { IMediaItem } from 'types/media'
import { pixelsToSeconds, secondsToPixels } from 'utils/timeline'
import TrackItem from './TrackItem'
import { dispatch } from 'store'
import { TIMELINE_CONTENT_WRAPPER_ID } from '../../consts'
import { useTrackHeight } from './useTrackHeight'
import * as Styled from './styled'
import { useItemClickHandler } from '../hooks/use-item-click-handler'
import { COLORS } from 'consts/colors'

const VisualMediaTracks = (props: { preventOnClick: boolean }) => {
  const { preventOnClick } = props
  const mediaItemsFromStore = useSelector(projectDataSelectors.mediaItems)
  const [mediaItems, setMediaItems] =
    useState<IMediaItem[]>(mediaItemsFromStore)

  useEffect(() => {
    setMediaItems(mediaItemsFromStore)
  }, [mediaItemsFromStore])

  const timelineRulerSliceDuration = useSelector(
    editorSelectors.timelineRulerSliceDuration
  )
  const activeTimelineItems = useSelector(editorSelectors.activeTimelineItems)

  const handler = useItemClickHandler()

  const dndData = useMemo(
    () =>
      mediaItems.map(item => {
        const { id, startTime, duration, zIndex, type } = item

        const isActive = activeTimelineItems.some(
          activeItem => activeItem.id === id && activeItem.type === type
        )
        const isResizeBtnsVisible = activeTimelineItems.length <= 1

        // disable dnd kit  dragging in multiple items dragging mode
        const isItemInMemberOfActiveItems = isActive && !isResizeBtnsVisible
        return {
          id,
          left: secondsToPixels(startTime, timelineRulerSliceDuration),
          width: secondsToPixels(duration, timelineRulerSliceDuration),
          lineIndex: zIndex,
          isDraggingDisabled: isItemInMemberOfActiveItems,
          content: (
            <TrackItem
              data={item}
              isActive={isActive}
              onClick={event => {
                if (!preventOnClick) {
                  handler({ id, type, event, isActive })
                }
              }}
              updateMediaItems={setMediaItems}
              isResizeBtnsVisible={isResizeBtnsVisible}
              selectedBorderColor={COLORS.primaryExtraDark}
              selectedBorderSize={4}
            />
          )
        }
      }),
    // TODO: Recheck this
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      mediaItems,
      timelineRulerSliceDuration,
      activeTimelineItems,
      preventOnClick
    ]
  )

  const handleItemsUpdate = (updatedItems: TDndListItems) => {
    const updatedMediaItems = updatedItems.map(updatedItem => {
      const mediaItem = mediaItems.find(item => item.id === updatedItem.id)

      if (!mediaItem) {
        return null
      }

      return {
        ...mediaItem,
        startTime: pixelsToSeconds(
          updatedItem.left,
          timelineRulerSliceDuration
        ),
        duration: pixelsToSeconds(updatedItem.width, timelineRulerSliceDuration)
      }
    })

    // TODO: Recheck this type casting, doesn't look safe
    dispatch(
      projectDataThunks.setMediaItems({
        value: updatedMediaItems as IMediaItem[]
      })
    )
  }

  const handleDragStart = () => {
    dispatch(editorThunks.setIsTimelineItemDragging(true))
  }

  const handleDragEnd = (updatedItems: TDndListItems) => {
    handleItemsUpdate(updatedItems)
    dispatch(editorThunks.setIsTimelineItemDragging(false))
  }

  const trackHeight = useTrackHeight()

  return (
    <Styled.VisualMediaTracks>
      <MultiLineDnd
        data={dndData}
        dropLineSettings={{
          style: {
            height: trackHeight
          },
          className: 'visual-media-track'
        }}
        isMultiLineAllowed={false}
        onDragEnd={handleDragEnd}
        onDragStart={handleDragStart}
        boundingContainerId={TIMELINE_CONTENT_WRAPPER_ID}
      />
    </Styled.VisualMediaTracks>
  )
}

export default memo(VisualMediaTracks)
